import React, { useEffect } from 'react';
import { Shield, AlertTriangle, Trash2, CheckCircle, Scale } from 'lucide-react';
import { apiClient } from '../../utils/apiClient';
import { API_URL } from '../../config';

const DisclaimerStep = ({ data, updateData, onNext, onCancel }) => {
  const canProceed = data.age18Plus && data.termsAccepted;
  
  const handleContinue = async () => {
    if (!canProceed) {
      alert('⚠️ Bitte akzeptiere beide Checkboxen, um fortzufahren.');
      return;
    }
    
    // DSGVO Consent-Logging beim Avatar-Start
    try {
      const user = JSON.parse(localStorage.getItem('snapshot_user') || '{}');
      const ipHash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode('client-ip-placeholder'))
        .then(buf => Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join(''));
      
      await apiClient.post('/api/consents', {
        user_id: user.id,
        email: user.email,
        avatar_id: null,
        policy_version: '2025-11-01',
        consent_type: 'avatar_creation',
        consents: {
          terms: false,
          privacy: false,
          imageProcessing: true,
          age: true
        },
        ip_hash: ipHash,
        timestamp: new Date().toISOString()
      });
      console.log('✅ Consent logged for avatar creation');
    } catch (err) {
      console.warn('⚠️ Consent logging failed (non-critical):', err);
    }
    
    onNext();
  };

  return (
    <div className="bg-black grain-texture text-white min-h-screen py-12 px-4">
      <div className="max-w-4xl mx-auto">
        
        <h1 className="text-3xl sm:text-4xl font-bold mb-3 text-center">
          Dein digitales Ich – <span className="text-[#10B981] glow-green">Die Richtlinien</span>
        </h1>
        <p className="text-white/70 text-center mb-8 text-base">
          Bitte lies diese Punkte aufmerksam durch und bestätige sie vor der Nutzung:
        </p>

        <div className="space-y-6 mb-8">
          
          {/* 1. Rechte & Berechtigungen */}
          <div className="p-6 border-l-4 border-blue-500 bg-blue-500/5 rounded-r-xl">
            <div className="flex items-start gap-3 mb-3">
              <Shield className="w-6 h-6 text-blue-400 flex-shrink-0 mt-1" />
              <h3 className="font-bold text-xl text-white">1. Rechte & Berechtigungen</h3>
            </div>
            <div className="text-white/80 text-sm leading-relaxed pl-9 space-y-2">
              <p>
                Ich bestätige, dass ich alle erforderlichen Rechte und Einwilligungen an den von mir hochgeladenen Bildern besitze.
              </p>
              <p>
                Ich erlaube SNAPSHOTPRO ausdrücklich, diese Bilder <strong>ausschließlich</strong> für die Erstellung und das Training meines persönlichen KI-Modells zu verwenden.
              </p>
              <p className="text-white/60 text-xs italic">
                Eine darüber hinausgehende Nutzung oder Weitergabe erfolgt nicht.
              </p>
            </div>
          </div>

          {/* 2. Verantwortung des Nutzers */}
          <div className="p-6 border-l-4 border-orange-500 bg-orange-500/5 rounded-r-xl">
            <div className="flex items-start gap-3 mb-3">
              <AlertTriangle className="w-6 h-6 text-orange-400 flex-shrink-0 mt-1" />
              <h3 className="font-bold text-xl text-white">2. Verantwortung des Nutzers</h3>
            </div>
            <div className="text-white/80 text-sm leading-relaxed pl-9 space-y-2">
              <p>
                Ich trage die <strong>alleinige Verantwortung</strong> für den Inhalt der von mir hochgeladenen Dateien.
              </p>
              <p>
                Ich versichere, dass meine Uploads keine Rechte Dritter (z. B. Urheber-, Marken- oder Persönlichkeitsrechte) verletzen.
              </p>
              <p>
                <strong className="text-orange-400">Wichtig:</strong> Uploads von <strong>realen Personen (inkl. Prominente, Politiker, etc.) ohne deren ausdrückliche Einwilligung sind verboten</strong> und führen zur sofortigen Sperrung des Accounts.
              </p>
              <p>
                Im Falle von Ansprüchen Dritter stelle ich SNAPSHOTPRO von allen daraus entstehenden Schäden frei.
              </p>
            </div>
          </div>

          {/* 3. Löschrichtlinie */}
          <div className="p-6 border-l-4 border-red-500 bg-red-500/5 rounded-r-xl">
            <div className="flex items-start gap-3 mb-3">
              <Trash2 className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" />
              <h3 className="font-bold text-xl text-white">3. Löschrichtlinie</h3>
            </div>
            <div className="text-white/80 text-sm leading-relaxed pl-9 space-y-2">
              <p>
                SNAPSHOTPRO behält sich das Recht vor, Uploads, die gegen geltendes Recht, die Nutzungsbedingungen oder diese Richtlinien verstoßen, jederzeit zu löschen.
              </p>
              <p>
                Ich kann meine Daten und erstellten Avatare jederzeit über mein Konto löschen lassen.
              </p>
            </div>
          </div>

          {/* 4. Altersgrenze & Einwilligung */}
          <div className="p-6 border-l-4 border-purple-500 bg-purple-500/5 rounded-r-xl">
            <div className="flex items-start gap-3 mb-3">
              <CheckCircle className="w-6 h-6 text-purple-400 flex-shrink-0 mt-1" />
              <h3 className="font-bold text-xl text-white">4. Altersgrenze & Einwilligung</h3>
            </div>
            <div className="text-white/80 text-sm leading-relaxed pl-9 space-y-2">
              <p>
                Ich bin mindestens 18 Jahre alt oder habe die Einwilligung meiner Erziehungsberechtigten.
              </p>
              <p>
                Ich habe die <a href="/agb" target="_blank" className="text-[#10B981] underline hover:text-[#059669]">Nutzungsbedingungen</a> und die <a href="/datenschutz" target="_blank" className="text-[#10B981] underline hover:text-[#059669]">Datenschutzerklärung</a> gelesen und stimme ihnen zu.
              </p>
            </div>
          </div>

          {/* 5. Datenschutz & KI-Verarbeitung */}
          <div className="p-6 border-l-4 border-[#10B981] bg-[#10B981]/5 rounded-r-xl">
            <div className="flex items-start gap-3 mb-3">
              <Scale className="w-6 h-6 text-[#10B981] flex-shrink-0 mt-1" />
              <h3 className="font-bold text-xl text-white">5. Datenschutz & KI-Verarbeitung</h3>
            </div>
            <div className="text-white/80 text-sm leading-relaxed pl-9 space-y-2">
              <p>
                Ich stimme zu, dass meine hochgeladenen Bilder technisch verarbeitet werden, um ein individuelles KI-Modell zu erstellen.
              </p>
              <p>
                Diese Verarbeitung erfolgt gemäß der DSGVO und ausschließlich auf Grundlage meiner ausdrücklichen Einwilligung.
              </p>
              <p>
                SNAPSHOTPRO verwendet meine Daten <strong>nicht</strong> zu Werbe-, Analyse- oder Weiterverkaufszwecken.
              </p>
            </div>
          </div>
        </div>

        {/* Bestätigung Box */}
        <div className="bg-white/5 border-2 border-white/20 rounded-xl p-6 mb-8">
          <h3 className="font-bold text-lg text-white mb-4 text-center">✅ Bestätigung</h3>
          
          <div className="space-y-4">
            <label className="flex items-start space-x-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={data.age18Plus}
                onChange={(e) => updateData('age18Plus', e.target.checked)}
                className="mt-1 w-5 h-5 rounded border-2 border-white/30 bg-black checked:bg-[#10B981] checked:border-[#10B981] focus:ring-2 focus:ring-[#10B981] cursor-pointer"
              />
              <span className="text-sm text-white/80 group-hover:text-white transition-colors leading-relaxed">
                Ich bin <strong>mindestens 18 Jahre alt</strong> oder habe die Einwilligung meiner Erziehungsberechtigten.
              </span>
            </label>

            <label className="flex items-start space-x-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={data.termsAccepted}
                onChange={(e) => updateData('termsAccepted', e.target.checked)}
                className="mt-1 w-5 h-5 rounded border-2 border-white/30 bg-black checked:bg-[#10B981] checked:border-[#10B981] focus:ring-2 focus:ring-[#10B981] cursor-pointer"
              />
              <span className="text-sm text-white/80 group-hover:text-white transition-colors leading-relaxed">
                Durch das Hochladen meiner Bilder bestätige ich, dass ich <strong>diese Richtlinien gelesen, verstanden und akzeptiert</strong> habe.
              </span>
            </label>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-between">
          <button
            onClick={onCancel}
            className="px-8 py-4 border-2 border-white/30 text-white rounded-xl hover:bg-white/5 hover:border-white/50 transition-all font-semibold"
          >
            Abbrechen
          </button>
          <button
            onClick={handleContinue}
            className={`px-8 py-4 rounded-xl font-bold transition-all ${
              canProceed
                ? 'bg-[#10B981] text-black hover:bg-[#059669] glow-green pulse-glow shine-effect cursor-pointer'
                : 'bg-white/10 text-white/40 cursor-not-allowed border-2 border-white/20 pointer-events-none'
            }`}
          >
            Weiter
          </button>
        </div>
      </div>
    </div>
  );
};

export default DisclaimerStep;
